import { useState } from 'react';
import { ExternalLink, Code2, Gamepad2, Globe, Database, Layers, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import TestimonialCard from '@/components/TestimonialCard';
import TestimonialModal from '@/components/TestimonialModal';

// ============================================
// STATIC TESTIMONIALS DATA
// ============================================
const testimonials = [
  {
    id: '1',
    title: 'Webstore Community',
    description: 'A fully-featured Webstore Community platform built with modern technologies. Includes real-time Live Chat functionality for instant customer support and community interaction. Developed using React for dynamic user interfaces, shadcn/ui for beautiful and accessible components, delivering a seamless shopping and social experience.',
    rating: 5,
    time_to_finish: '7 Days',
    image_urls: ['https://i.ibb.co.com/gLwD8DvX/image-2025-12-31-044703665.png'],
    published: true,
  },
  {
    id: '2',
    title: 'License System Management',
    description: 'A comprehensive License System Management dashboard with enterprise-grade security. Features fully protected backend architecture, RESTful API integration, and robust license validation systems. Built with React, Radix UI, Tailwind CSS, shadcn/ui, and HeroUI for a modern, responsive admin interface with complete CRUD operations.',
    rating: 5,
    time_to_finish: '3 Days',
    image_urls: ['https://i.ibb.co.com/r2LQjDHf/image-2025-12-31-044912576.png'],
    published: true,
  },
  {
    id: '3',
    title: 'Photobooth Website',
    description: 'An interactive Photobooth Website with stunning animations and smooth user experience. Powered by Next.js and React for optimal performance, featuring Framer Motion for fluid animations, Radix UI for accessible components, Tailwind CSS for styling, and shadcn/ui for a polished, modern interface.',
    rating: 5,
    time_to_finish: '5 Days',
    image_urls: ['https://i.ibb.co.com/kVfCHSnN/image-2025-12-31-045142514.png'],
    published: true,
  },
];

const skills = [
  { icon: Globe, name: 'Full-Stack Web', description: 'React, Next.js, Node.js' },
  { icon: Gamepad2, name: 'Roblox Studio', description: 'Lua, Game Systems' },
  { icon: Database, name: 'Backend & APIs', description: 'REST API, Database' },
  { icon: Code2, name: 'Frontend', description: 'React, Tailwind, shadcn/ui' },
  { icon: Layers, name: 'System Design', description: 'Scalable Architecture' },
];

const LandingPage = () => {
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleTestimonialClick = (testimonial) => {
    setSelectedTestimonial(testimonial);
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Hero Section */}
      <section className="hero-bg min-h-screen flex items-center relative overflow-hidden" data-testid="hero-section">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 py-20 relative z-10">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-6 opacity-0 animate-fade-in-up">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="mono text-xs text-zinc-400 uppercase tracking-widest">Available for Projects</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold mb-6 opacity-0 animate-fade-in-up stagger-1">
              <span className="gradient-text">Luminare Studio</span>
            </h1>
            
            <p className="text-lg md:text-xl text-zinc-400 mb-8 leading-relaxed opacity-0 animate-fade-in-up stagger-2">
              <span className="text-white font-medium">Full-Stack Web Development & Roblox Studio</span> brand 
              focused on building production-ready systems that turn visitors into users — from modern 
              web applications to immersive game experiences.
            </p>
            
            <div className="flex flex-wrap gap-4 opacity-0 animate-fade-in-up stagger-3">
              <a 
                href="https://www.fiverr.com/s/LdgZpaL" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button 
                  className="cta-button px-8 py-6 text-base font-semibold rounded-full"
                  data-testid="cta-button"
                >
                  Hire Me on Fiverr
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <a href="#testimonials">
                <Button 
                  variant="outline" 
                  className="px-8 py-6 text-base font-semibold rounded-full border-white/10 bg-white/5 hover:bg-white/10 text-white"
                  data-testid="view-work-btn"
                >
                  View My Work
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>
          </div>
        </div>
        
        {/* Floating decoration */}
        <div className="absolute right-10 top-1/2 -translate-y-1/2 w-96 h-96 hidden lg:block">
          <div className="relative w-full h-full">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-purple-500/20 rounded-full blur-3xl animate-pulse" />
            <div className="absolute top-10 left-10 w-4 h-4 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
            <div className="absolute bottom-20 right-20 w-3 h-3 bg-accent rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
            <div className="absolute top-1/2 right-0 w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0.5s' }} />
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section className="py-20 border-t border-white/5" data-testid="skills-section">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="mb-12">
            <span className="mono text-xs text-primary uppercase tracking-widest">Expertise</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-2">What I Build</h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {skills.map((skill, index) => (
              <div
                key={skill.name}
                className="skill-badge p-5 rounded-xl text-center group hover:-translate-y-1 transition-all duration-300"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`skill-${skill.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <skill.icon className="w-8 h-8 mx-auto mb-3 text-primary group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold text-white text-sm mb-1">{skill.name}</h3>
                <p className="text-xs text-zinc-500">{skill.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 border-t border-white/5" data-testid="testimonials-section">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-start justify-between mb-12">
            <div>
              <span className="mono text-xs text-primary uppercase tracking-widest">Client Success</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mt-2">Project Showcase</h2>
              <p className="text-zinc-400 mt-2 max-w-lg">
                Real projects delivered. Real results achieved. Click any card to see the full story.
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.id}
                className="opacity-0 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s`, animationFillMode: 'forwards' }}
              >
                <TestimonialCard
                  testimonial={testimonial}
                  onClick={() => handleTestimonialClick(testimonial)}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 border-t border-white/5" data-testid="cta-section">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="glass rounded-2xl p-8 md:p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-purple-500/10" />
            <div className="relative z-10">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Ready to Build Something Amazing?
              </h2>
              <p className="text-zinc-400 mb-8 max-w-lg mx-auto">
                Turn your vision into reality. Professional, reliable, and production-ready development.
              </p>
              <a 
                href="https://www.fiverr.com/s/LdgZpaL" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button 
                  className="cta-button px-10 py-6 text-base font-semibold rounded-full"
                  data-testid="footer-cta-button"
                >
                  Start a Project
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-white/5">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-zinc-500 text-sm">
              © 2024 Luminare Studio. All rights reserved.
            </p>
            <a 
              href="https://www.fiverr.com/s/LdgZpaL" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-zinc-500 hover:text-primary text-sm transition-colors"
            >
              Contact on Fiverr
            </a>
          </div>
        </div>
      </footer>

      {/* Testimonial Modal */}
      <TestimonialModal
        testimonial={selectedTestimonial}
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
      />
    </div>
  );
};

export default LandingPage;
